import { api } from '../client';

import type { ListResponse, PaginationParams, SuccessResponse } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

/** Canonical inventory status returned by the API. */
export type InventoryStatus = 'draft' | 'confirmed';

/** Persisted status exposed separately for audit/compatibility context. */
export type InventoryLegacyStatus = 'in_progress' | 'completed';

/** Inventory header */
export interface Inventory {
  id: string;
  location_id: string;
  inventory_type: 'full' | 'partial';
  status: InventoryStatus;
  legacy_status?: InventoryLegacyStatus | null;
  current_revision_no?: number | null;
  current_revision_id?: string | null;
  snapshot_date?: string | null;
  snapshot_at?: string | null;
  reopened_from_inventory_id?: string | null;
  corrected_by_inventory_id?: string | null;
  voided_at?: string | null;
  voided_by?: string | null;
  void_reason?: string | null;
  can_edit?: boolean;
  can_cancel?: boolean;
  can_correct?: boolean | null;
  can_edit_metadata?: boolean | null;
  movements_after_count?: number;
  blocked_reason?: string | null;
  first_blocking_movement_effective_date?: string | null;
  first_blocking_movement_effective_at?: string | null;
  items_total?: number;
  items_filled?: number;
  discrepancy_count?: number | null;
  unit_totals?: Record<string, string> | null;
  estimated_value_total_pln?: string | null;
  ocr_summary?: InventoryOcrSummary | null;
  category_ids?: string[] | null;
  subcategory_ids?: string[] | null;
  started_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  created_by?: string | null;
  /** MD-0108: sent for approval from the till (only an admin confirms). */
  submitted_at?: string | null;
  submitted_by?: string | null;
}

export interface InventoryOcrSummary {
  status: string;
  total_pages: number;
  uploaded_pages: number;
  processing_pages: number;
  completed_pages: number;
  failed_pages: number;
  stale_reason?: string | null;
  last_started_at?: string | null;
  last_completed_at?: string | null;
}

/** Inventory item */
export interface InventoryItem {
  id: string;
  inventory_id: string;
  product_id: string;
  can_edit_actual_quantity?: boolean;
  blocked_reason?: string | null;
  expected_quantity: string;
  actual_quantity?: string | null;
  difference?: string | null;
}

/** Inventory with items */
export interface InventoryDetail extends Inventory {
  items: InventoryItem[];
}

export interface InventoryDetailResponse {
  request_id: string;
  inventory: InventoryDetail;
  movements_created?: number;
  already_completed?: boolean;
  already_cancelled?: boolean;
}

/** Inventory filters */
export interface InventoryFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  location_id?: string;
  status?: string;
  inventory_type?: string;
  date_from?: string;
  date_to?: string;
  /** MD-0108: only inventories sent for approval from the till. */
  awaiting_approval?: boolean;
}

/** Inventories response */
export type InventoriesResponse = ListResponse<Inventory>;

/** Create inventory request */
export interface CreateInventoryRequest {
  location_id: string;
  inventory_type: 'full' | 'partial';
  category_ids?: string[];
  /** ERP subcategories (shelf groups) counted in addition to whole categories. */
  subcategory_ids?: string[];
  snapshot_date?: string;
  snapshot_at?: string;
}

/** Update inventory item request */
export interface UpdateInventoryItemRequest {
  product_id: string;
  actual_quantity: string;
}

/** Inventory metadata impact summary returned by preview/apply endpoints. */
export interface InventoryMetadataImpact {
  rows_added: number;
  rows_removed: number;
  rows_expected_changed: number;
  counted_rows_out_of_scope: number;
  affected_counted_rows: number;
  location_changed: boolean;
  inventory_type_changed: boolean;
  snapshot_changed: boolean;
  categories_changed: boolean;
  has_conflict: boolean;
  existing_draft_id?: string | null;
}

/** Apply metadata changes to a draft inventory. */
export interface InventoryMetadataApplyRequest {
  location_id?: string;
  inventory_type?: 'full' | 'partial';
  category_ids?: string[];
  subcategory_ids?: string[];
  snapshot_date?: string;
  snapshot_at?: string;
  item_updates?: UpdateInventoryItemRequest[];
  change_reason?: string;
}

/** Preview inventory metadata changes before apply/revise. */
export interface InventoryMetadataPreviewRequest {
  location_id?: string;
  inventory_type?: 'full' | 'partial';
  category_ids?: string[];
  subcategory_ids?: string[];
  snapshot_date?: string;
  snapshot_at?: string;
  item_updates?: UpdateInventoryItemRequest[];
}

/** Inventory metadata state in preview response (current/proposed). */
export interface InventoryMetadataState {
  location_id: string;
  inventory_type: 'full' | 'partial';
  category_ids?: string[] | null;
  snapshot_date: string;
  snapshot_at: string;
  items_total: number;
  items_filled: number;
}

/** Payload of inventory metadata preview endpoint. */
export interface InventoryMetadataPreviewPayload {
  inventory_id: string;
  status: string;
  current: InventoryMetadataState;
  proposed: InventoryMetadataState;
  impact: InventoryMetadataImpact;
}

/** Response of metadata-preview endpoint. */
export interface InventoryMetadataPreviewResponse {
  request_id: string;
  preview: InventoryMetadataPreviewPayload;
}

/** Response of metadata-apply endpoint. */
export interface InventoryMetadataApplyResponse {
  request_id: string;
  inventory: InventoryDetail;
  impact: InventoryMetadataImpact;
}

/** Revise request for confirmed inventories (same user save flow). */
export interface ReviseInventoryRequest {
  location_id?: string;
  inventory_type?: 'full' | 'partial';
  category_ids?: string[];
  subcategory_ids?: string[];
  snapshot_date?: string;
  snapshot_at?: string;
  item_updates?: UpdateInventoryItemRequest[];
  change_reason?: string;
}

/** Product metadata for inventory (names, categories, barcodes) */
export interface InventoryProductMeta {
  product_id: string;
  inventory_form_code: string;
  name: string;
  unit_code: string | null;
  is_weighted: boolean;
  category_id?: string | null;
  category_name?: string | null;
  barcodes: string[];
}

/** Response for inventory products-meta endpoint */
export interface InventoryProductMetaResponse {
  request_id: string;
  items: InventoryProductMeta[];
}

export interface InventoryValuationItem {
  unit_cost: string | null;
  cost_source: 'recipe' | 'purchase' | null;
  sale_price: string | null;
  is_cost_partial: boolean;
}

export interface InventoryValuationResponse {
  request_id: string;
  items: Record<string, InventoryValuationItem>;
}

export type InventoryOcrSuggestionState =
  | 'suggested'
  | 'approved'
  | 'edited'
  | 'rejected'
  | 'conflict'
  | (string & {});

export type InventoryOcrPageStatus =
  | 'uploaded'
  | 'processing'
  | 'completed'
  | 'failed'
  | (string & {});

export interface InventoryOcrProvenance {
  page_index?: number;
  model_used?: string | null;
  candidate_order?: number;
  [key: string]: unknown;
}

export interface InventoryOcrSourceRef {
  page_index?: number | null;
  box_2d?: number[] | null;
  text?: string | null;
  confidence?: number | null;
}

export type InventoryOcrQuantityMark = 'value' | 'dash' | 'empty' | 'unreadable';

export interface InventoryOcrCandidate {
  product_code?: string | null;
  product_code_raw?: string | null;
  row_no?: number | null;
  product_name_raw?: string | null;
  unit_raw?: string | null;
  quantity_mark?: InventoryOcrQuantityMark | null;
  quantity_raw?: string | null;
  comment_raw?: string | null;
  confidence?: number | null;
  parse_diagnostics?: string[];
  authoritative_quantity?: string | null;
  authoritative_quantity_source?: string | null;
  source_refs?: InventoryOcrSourceRef[];
  provenance?: InventoryOcrProvenance | null;
}

export interface InventoryOcrResolution {
  action?: 'accept' | 'reject' | 'manual' | string;
  resolved_at?: string | null;
  resolved_by?: string | null;
  resolution_source?: 'ocr_applied' | 'manual' | string;
  final_quantity?: string | null;
  raw_ocr_value?: string | null;
  provenance?: InventoryOcrProvenance | null;
  manual_comment?: string | null;
  [key: string]: unknown;
}

export interface InventoryOcrSuggestion {
  product_code?: string | null;
  product_code_raw?: string | null;
  row_no?: number | null;
  state: InventoryOcrSuggestionState;
  confidence?: number | null;
  product_name_raw?: string | null;
  unit_raw?: string | null;
  quantity_mark?: InventoryOcrQuantityMark | null;
  quantity_raw?: string | null;
  comment_raw?: string | null;
  suggested_quantity?: string | null;
  suggested_quantity_source?: string | null;
  parse_diagnostics?: string[];
  candidates?: InventoryOcrCandidate[];
  source_refs?: InventoryOcrSourceRef[];
  resolution?: InventoryOcrResolution | null;
  provenance?: InventoryOcrProvenance | null;
  [key: string]: unknown;
}

export interface InventoryOcrPage {
  id: string;
  page_index: number;
  status: InventoryOcrPageStatus;
  preview_url: string;
  original_filename?: string | null;
  mime_type?: string | null;
  last_run_id?: string | null;
  model_used?: string | null;
  timeout_seconds?: number | null;
  attempt_count: number;
  failure_class?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  ocr_failures?: Array<Record<string, unknown>>;
  row_count: number;
  extracted_rows?: Array<Record<string, unknown>>;
  page_confidence?: string | null;
  diagnostics?: Record<string, unknown>;
}

export interface InventoryOcrSession {
  status: string;
  run_id?: string | null;
  current_run_id?: string | null;
  run_count: number;
  total_pages: number;
  processed_pages: number;
  failed_pages: number;
  queued_pages?: number;
  retry_wait_pages?: number;
  parallelism_effective?: number | null;
  stale_reason?: string | null;
  model_chain: string[];
  last_started_at?: string | null;
  last_completed_at?: string | null;
  last_run_meta?: Record<string, unknown>;
}

export interface InventoryOcrState {
  inventory_id: string;
  session: InventoryOcrSession;
  pages: InventoryOcrPage[];
  suggestions: InventoryOcrSuggestion[];
  merge_diagnostics?: Record<string, unknown>;
}

export interface InventoryOcrStateEnvelopeResponse {
  request_id: string;
  ocr: InventoryOcrState;
}

export interface InventoryOcrReorderRequest {
  ordered_page_ids: string[];
}

export interface InventoryOcrDeleteRequest {
  page_ids: string[];
}

export interface InventoryOcrRunRequest {
  rerun_failed_only?: boolean;
  overwrite_manual?: boolean;
  page_ids?: string[];
  parallelism_hint?: number;
}

export interface InventoryOcrResolveActionRequest {
  row_no?: number;
  product_code?: string;
  action: 'accept' | 'reject' | 'manual';
  candidate_index?: number;
  manual_quantity?: string;
  comment?: string;
}

export interface InventoryOcrResolveRequest {
  actions: InventoryOcrResolveActionRequest[];
}

type InventoryStockDocumentEntity = 'inventory';

export type StockInventoriesApiDeps = StockDocumentMutationDeps<InventoryStockDocumentEntity>;

export const createStockInventoriesApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockInventoriesApiDeps) => ({
  list: async (filters: InventoryFilters = {}): Promise<InventoriesResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'inventory',
      await api.webAdmin.get<InventoriesResponse>('/stock/inventories', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<InventoryDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'inventory',
      await api.webAdmin.get<InventoryDetailResponse>(`/stock/inventories/${id}`),
      (response) => response.inventory.id,
      (response) => response.inventory.updated_at,
    );
  },

  create: (
    data: CreateInventoryRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .post<InventoryDetailResponse>('/stock/inventories', data, options)
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  updateItems: (
    inventoryId: string,
    items: UpdateInventoryItemRequest[],
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .put<InventoryDetailResponse>(
        `/stock/inventories/${inventoryId}/items`,
        items,
        withExpectedUpdatedAt('inventory', inventoryId, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  /** MD-0108: give an inventory sent from the till back to the store (admin). */
  returnToTill: (id: string): Promise<InventoryDetailResponse> =>
    api.webAdmin.post<InventoryDetailResponse>(`/stock/inventories/${id}/return-to-till`),

  confirm: (id: string, options?: StockDocumentMutationOptions): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .post<InventoryDetailResponse>(
        `/stock/inventories/${id}/confirm`,
        undefined,
        withExpectedUpdatedAt('inventory', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  complete: (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .post<InventoryDetailResponse>(
        `/stock/inventories/${id}/complete`,
        undefined,
        withExpectedUpdatedAt('inventory', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  revise: (
    id: string,
    data: ReviseInventoryRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .post<InventoryDetailResponse>(
        `/stock/inventories/${id}/revise`,
        data,
        withExpectedUpdatedAt('inventory', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  metadataPreview: (
    inventoryId: string,
    data: InventoryMetadataPreviewRequest,
  ): Promise<InventoryMetadataPreviewResponse> =>
    api.webAdmin.post(`/stock/inventories/${inventoryId}/metadata-preview`, data),

  metadataApply: (
    inventoryId: string,
    data: InventoryMetadataApplyRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryMetadataApplyResponse> =>
    api.webAdmin
      .post<InventoryMetadataApplyResponse>(
        `/stock/inventories/${inventoryId}/metadata-apply`,
        data,
        withExpectedUpdatedAt('inventory', inventoryId, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  cancel: (
    id: string,
    data: { cancel_reason?: string | null } = {},
    options?: StockDocumentMutationOptions,
  ): Promise<InventoryDetailResponse> =>
    api.webAdmin
      .post<InventoryDetailResponse>(
        `/stock/inventories/${id}/cancel`,
        data,
        withExpectedUpdatedAt('inventory', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'inventory',
          response,
          (current) => current.inventory.id,
          (current) => current.inventory.updated_at,
        ),
      ),

  delete: (id: string, options?: StockDocumentMutationOptions): Promise<SuccessResponse> =>
    api.webAdmin
      .delete<SuccessResponse>(
        `/stock/inventories/${id}`,
        withExpectedUpdatedAt('inventory', id, options),
      )
      .then((response) => {
        forgetStockDocumentUpdatedAt('inventory', id);
        return response;
      }),

  productsMeta: (inventoryId: string): Promise<InventoryProductMetaResponse> =>
    api.webAdmin.get(`/stock/inventories/${inventoryId}/products-meta`),

  getValuation: (inventoryId: string): Promise<InventoryValuationResponse> =>
    api.webAdmin.get(`/stock/inventories/${inventoryId}/valuation`),

  inventoryFormTemplatePdfUrl: (filters: { category_ids?: string[] | null } = {}): string => {
    const params = new URLSearchParams();
    for (const categoryId of filters.category_ids ?? []) {
      params.append('category_ids', categoryId);
    }
    const query = params.toString();
    return `/api/web-admin/stock/inventories/form-template/pdf${query ? `?${query}` : ''}`;
  },
  inventoryReconciliationXlsxUrl: (inventoryId: string): string =>
    `/api/web-admin/stock/inventories/${inventoryId}/reconciliation/xlsx`,
  inventoryDiscrepanciesXlsxUrl: (
    inventoryId: string,
    options: {
      min_abs_difference?: string | number;
      difference_sign?: 'all' | 'positive' | 'negative';
    } = {},
  ): string => {
    const params = new URLSearchParams();
    if (options.min_abs_difference !== undefined) {
      params.set('min_abs_difference', String(options.min_abs_difference));
    }
    if (options.difference_sign) {
      params.set('difference_sign', options.difference_sign);
    }
    const query = params.toString();
    return `/api/web-admin/stock/inventories/${inventoryId}/discrepancies/xlsx${query ? `?${query}` : ''}`;
  },

  uploadOcrPages: (
    inventoryId: string,
    files: File[],
  ): Promise<InventoryOcrStateEnvelopeResponse> => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return api.webAdmin.post<InventoryOcrStateEnvelopeResponse>(
      `/stock/inventories/${inventoryId}/ocr/pages`,
      formData,
    );
  },

  reorderOcrPages: (
    inventoryId: string,
    data: InventoryOcrReorderRequest,
  ): Promise<InventoryOcrStateEnvelopeResponse> =>
    api.webAdmin.post(`/stock/inventories/${inventoryId}/ocr/pages/reorder`, data),

  deleteOcrPages: (
    inventoryId: string,
    data: InventoryOcrDeleteRequest,
  ): Promise<InventoryOcrStateEnvelopeResponse> =>
    api.webAdmin.post(`/stock/inventories/${inventoryId}/ocr/pages/delete`, data),

  getOcrState: (inventoryId: string): Promise<InventoryOcrStateEnvelopeResponse> =>
    api.webAdmin.get(`/stock/inventories/${inventoryId}/ocr`),

  runOcr: (
    inventoryId: string,
    data: InventoryOcrRunRequest,
    options?: { idempotencyKey?: string },
  ): Promise<InventoryOcrStateEnvelopeResponse> =>
    api.webAdmin.post(`/stock/inventories/${inventoryId}/ocr/run`, data, options),

  resolveOcr: (
    inventoryId: string,
    data: InventoryOcrResolveRequest,
    options?: { idempotencyKey?: string },
  ): Promise<InventoryOcrStateEnvelopeResponse> =>
    api.webAdmin.post(`/stock/inventories/${inventoryId}/ocr/resolve`, data, options),
});
